import { convertUnitValues } from 'plugin-assets/shared/normalization/data-management/ChannelsManagement/utils'

import {
    DEFAULT_PRECISION,
    HIGHEST_SEVERITY,
    SECOND_HIGHEST_SEVERITY,
    MUTED_WIDGET_LOCAL_STORAGE_BASEPATH
} from './constants'

export function formatNumber(value, unitSetChannel, standardChannel, useUnitSetPrecisionIfAvailable = true) {
    const unitSetPrecision =
        unitSetChannel && (unitSetChannel.precision || unitSetChannel.precision === 0) ? unitSetChannel.precision : null
    const standardChannelPrecision =
        standardChannel && (standardChannel.precision || standardChannel.precision === 0)
            ? standardChannel.precision
            : null
    const precision = useUnitSetPrecisionIfAvailable
        ? unitSetPrecision || standardChannelPrecision || DEFAULT_PRECISION
        : standardChannelPrecision || DEFAULT_PRECISION

    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision)
}

export function getHighestSeverity(alarms) {
    let currentHighest = ''

    for (const alarm of alarms) {
        if (alarm.severity === HIGHEST_SEVERITY) {
            return alarm.severity
        }

        if (currentHighest !== SECOND_HIGHEST_SEVERITY) {
            currentHighest = alarm.severity
        }
    }

    return currentHighest
}

export function localStoragePath(dashboardId, widgetId) {
    return MUTED_WIDGET_LOCAL_STORAGE_BASEPATH + `${dashboardId}/${widgetId}`
}

export async function convertRule(rule, targetUnitSet, standardCurves) {
    if (!rule || !rule.conditions) return rule

    return {
        ...rule,
        conditions: await Promise.all(
            rule.conditions.map(async cond => {
                const sourceUnit = cond.uom || standardCurves?.[cond.channel]?.unit || ''
                const targetUnit =
                    targetUnitSet?.data?.[cond.channel]?.unit || standardCurves?.[cond.channel]?.unit || ''

                if (sourceUnit && targetUnit && sourceUnit !== targetUnit) {
                    const isRange = Array.isArray(cond.threshold)
                    const converted = await Promise.all(
                        (isRange ? cond.threshold : [cond.threshold]).map(val =>
                            convertUnitValues({
                                sourceUnit,
                                targetUnit,
                                sourceValue: val
                            })
                        )
                    )

                    return {
                        ...cond,
                        threshold: isRange
                            ? converted.map((convertedVal, idx) => convertedVal.targetValue ?? cond.threshold[idx])
                            : converted[0].targetValue ?? cond.threshold,
                        uom: converted[0].targetUnit
                    }
                }

                return cond
            })
        )
    }
}

export const resizeObserverHandler = (
    observed,
    sideview,
    mustSave,
    collapsed,
    setNumberOfPills,
    setShowPills,
    setClipText
) => {
    if (sideview || (mustSave && collapsed) || document.body.clientWidth < 1200) {
        setShowPills(false)

        return
    }
    setShowPills(true)

    const totalWidthAvailable = observed.contentRect.width
    const textWidth = observed.target.children[2].children[0].getBoundingClientRect().width
    const pillWidths = [...observed.target.children[2].children[1].children]
        .slice(0, -1)
        .map(pill => pill.getBoundingClientRect().width)
    const extraPillWidth = [...observed.target.children[2].children[1].children].slice(-1)[0].getBoundingClientRect()
        .width

    const totalPillWidth = pillWidths.reduce((sum, width) => sum + width + 5, 0)
    const totalWidthFull = textWidth + totalPillWidth

    // everything fits without changes
    if (totalWidthFull <= totalWidthAvailable) {
        setClipText(false)
        setNumberOfPills(pillWidths.length)

        return
    }

    // everything fits if text is clipped
    if (totalPillWidth <= totalWidthAvailable * 0.3) {
        setClipText(true)
        setNumberOfPills(pillWidths.length)

        return
    }

    // at least one pill + an extra pill fit
    if (textWidth + pillWidths[0] + 5 + extraPillWidth + 5 <= totalWidthAvailable) {
        let shouldClipText = false
        let totalWidth = textWidth + pillWidths[0] + 5 + extraPillWidth + 5
        let i = 1
        for (i = 1; i < pillWidths.length; i++) {
            if (!shouldClipText && totalWidth + pillWidths[i] + 5 <= totalWidthAvailable) {
                totalWidth += pillWidths[i] + 5
            } else if (totalWidth - (shouldClipText ? 0 : textWidth) + pillWidths[i] + 5 <= totalWidthAvailable * 0.3) {
                totalWidth += pillWidths[i] + 5
                shouldClipText = true
            } else {
                break
            }
        }

        setNumberOfPills(i)
        setClipText(shouldClipText)

        return
    }

    // at least one pill + an extra pill fit if the text is clipped
    if (pillWidths[0] + 5 + extraPillWidth + 5 <= totalWidthAvailable * 0.3) {
        setClipText(true)

        let totalWidth = pillWidths[0] + 5 + extraPillWidth + 5
        let i = 1
        for (i = 1; i < pillWidths.length; i++) {
            if (totalWidth + pillWidths[i] + 5 <= totalWidthAvailable * 0.3) {
                totalWidth += pillWidths[i] + 5
            } else {
                break
            }
        }

        setNumberOfPills(i)

        return
    }

    setNumberOfPills(0)
    setClipText(textWidth > totalWidthAvailable)
}
